import { useActiveConsentPurposes } from '@/hooks/queries/useConsentProviders';
import { BaseField } from '@dynamic-flow/types';

// 5. Consent Field Configuration Component
interface ConsentConfigProps {
  field: BaseField;
  onUpdate: (updates: Partial<BaseField>) => void;
  availableFields: BaseField[]; // All fields in the form
  disabled?: boolean;
}

export function ConsentConfig({
  field,
  onUpdate,
  availableFields,
  disabled,
}: ConsentConfigProps) {
  const { data: purposes, isLoading } = useActiveConsentPurposes();

  const consentConfig = field.consentConfig || {
    purposeId: '',
    identifierField: undefined,
  };

  const identifierFields = availableFields.filter(
    (f) => f.id !== field.id && ['email_input', 'phone_input'].includes(f.type)
  );

  return (
    <div className="space-y-4">
      {/* Purpose Selection */}
      <div>
        <label className="block text-sm font-medium text-gray-700 mb-1">
          Consent Purpose *
        </label>
        <select
          value={consentConfig.purposeId}
          onChange={(e) => {
            if (disabled) {
              return;
            }
            onUpdate({
              consentConfig: {
                ...consentConfig,
                purposeId: e.target.value,
              },
            });
          }}
          className="w-full px-3 py-2 border border-gray-300 rounded-md text-sm"
          disabled={isLoading || disabled}
        >
          <option value="">Select consent purpose</option>
          {purposes?.map((purpose) => (
            <option key={purpose.id} value={purpose.purposeId}>
              {purpose.name}
            </option>
          ))}
        </select>
        {consentConfig.purposeId && (
          <p className="text-xs text-gray-500 mt-1">
            {
              purposes?.find((p) => p.purposeId === consentConfig.purposeId)
                ?.description
            }
          </p>
        )}
      </div>

      {/* Identifier Field Selection */}
      <div>
        <label className="block text-sm font-medium text-gray-700 mb-1">
          Identifier Field *
          <span className="text-gray-500 text-xs ml-1">
            (Email or Phone for consent tracking)
          </span>
        </label>
        <select
          value={consentConfig.identifierField || ''}
          disabled={isLoading || disabled}
          onChange={(e) => {
            if (disabled) {
              return;
            }
            onUpdate({
              consentConfig: {
                ...consentConfig,
                identifierField: e.target.value || undefined,
              },
            });
          }}
          className="w-full px-3 py-2 border border-gray-300 rounded-md text-sm"
        >
          <option value="">Select identifier field</option>
          {identifierFields.map((f) => (
            <option key={f.id} value={f.id}>
              {f.label} ({f.type === 'email_input' ? 'Email' : 'Phone'})
            </option>
          ))}
        </select>
        {identifierFields.length === 0 && (
          <p className="text-xs text-amber-600 mt-1">
            ⚠️ Add an email or phone field first
          </p>
        )}
      </div>

      {/* Purpose Details */}
      {consentConfig.purposeId && (
        <div className="p-3 bg-blue-50 border border-blue-200 rounded-md">
          <h4 className="text-sm font-medium text-blue-900 mb-1">
            Purpose Configuration
          </h4>
          <dl className="text-xs space-y-1">
            <div>
              <dt className="inline font-medium text-blue-800">Purpose ID:</dt>
              <dd className="inline ml-1 text-blue-600">
                {
                  purposes?.find((p) => p.purposeId === consentConfig.purposeId)
                    ?.purposeId
                }
              </dd>
            </div>
          </dl>
        </div>
      )}
    </div>
  );
}

// 6. Render Consent Field in Form
export function renderConsentField(field: BaseField, question: any) {
  return (
    <div className="space-y-2">
      <div className="flex items-start space-x-2">
        <input {...field} className="mt-1" />
        <label htmlFor={field.id} className="text-sm flex-1">
          {question.label}
          {question.required && <span className="text-red-500 ml-1">*</span>}
        </label>
      </div>

      {question.fieldState?.error && (
        <p className="form-error-message text-sm mt-1 ml-6">
          {question.fieldState.error.message}
        </p>
      )}
    </div>
  );
}
